import { useCallback, useEffect, useRef, useState } from 'react'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import * as api from '../lib/api'
import { useCanAct } from '../lib/access'
import Icon from '../components/Icon'
import ReconciliationTracker from '../components/ReconciliationTracker'
import SampleTag from '../components/SampleTag'
import { isSampleId } from '../lib/samples'

/** Turns a `checkReconciliationBatch`/`getReconciliationBatchStatus` response
 *  into one toast-sized line — what a "Check status" press on a History row
 *  reports. */
function describeBatchStatus(out) {
  if (!out.finalized) return { kind: 'info', message: `Still processing (Anthropic: ${out.processingStatus})` }
  if (out.status === 'success') return { kind: 'ok', message: 'Finished — reconciliation succeeded, ready to download.' }
  if (out.outcome === 'cancelled') return { kind: 'bad', message: 'This batch was cancelled before it produced a result.' }
  if (out.outcome === 'batch_expired') return { kind: 'bad', message: "This batch didn't complete within Anthropic's 24-hour window." }
  return { kind: 'bad', message: `Ended without a usable result (${out.failureReason ?? out.outcome}).` }
}

const MODE_OPTIONS = [
  { value: 'tracker', label: 'Client tracker' },
  { value: 'testing', label: 'Test bench' },
]

const HISTORY_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'success', label: 'Succeeded' },
  { value: 'failure', label: 'Failed' },
  { value: 'pending', label: 'In progress' },
]

/**
 * A few short options, all visible, one selected — same shape and CSS
 * (`.seg`/`.seg-b`) as `LedgerCollector.jsx`'s own `Segmented`, duplicated
 * rather than imported since that one is local to its own file. A cycling
 * button would hide the other choices; this keeps "Failed" visible even
 * while "All" is selected, which is the point of a filter someone can see.
 */
function Segmented({ options, value, onChange, title }) {
  return (
    <div className="seg" role="radiogroup" title={title}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={`seg-b${o.value === value ? ' on' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** `8 Sep, 2:14 pm` — a run history needs minute granularity, which none of
 *  lib/when.js's day-granularity helpers give. */
function runTimeText(iso) {
  const d = new Date(iso)
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })
}

/**
 * A test bench for one question: can Claude reconcile two ledgers well
 * enough to be worth building for real?
 *
 * Deliberately standalone — no connection to the ledger collector, no
 * LedgerRequest, no stored client data. Upload any two files (Binny's own
 * ledger, and a client's) and see what the accounts team's own
 * ledger-reconciliation skill produces, run through Claude instead of a
 * person. See lereco-backend/services/ledgerReconcile.service.js and
 * lereco-backend/services/skills/ledger-reconciliation/SKILL.md.
 */

/**
 * Claude's summary is real markdown (headers, bold, tables) — it was being
 * shown as raw text in a `<pre>` block, asterisks and pipe characters and
 * all. `marked` renders it; `DOMPurify` sanitizes the result before it goes
 * into the DOM, since the underlying content ultimately derives from a
 * client's own ledger narrations passing through Claude's output — not
 * something to trust as safe HTML just because it came back from the model.
 */
function Markdown({ text }) {
  if (!text) return null
  const html = DOMPurify.sanitize(marked.parse(text, { async: false }))
  return <div className="md" dangerouslySetInnerHTML={{ __html: html }} />
}

/** Copies `text` to the clipboard, with a brief "Copied" confirmation rather
 *  than trusting the click alone — the clipboard has no visible state of its
 *  own to glance at. */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can be denied by the browser — nothing to recover
      // into here beyond just not claiming success.
    }
  }

  return (
    <button type="button" className="btn btn-sm btn-ghost reconcile-copy" onClick={copy}>
      <Icon name={copied ? 'check' : 'copy'} size={12} />
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

/**
 * What Claude learned about *this specific client* while doing this run —
 * their voucher-prefix conventions, which TDS section applies, anything a
 * future reconciliation of the same client should walk in already knowing.
 * Same shape as the accounts team's own RECONCILIATION_LEARNINGS.md uses for
 * IGI/Argentum/IHA (Findings / Key Issues / Lesson), but genuinely generated
 * for this run — not a copy of that static file, which is what an earlier,
 * wrong version of this feature did.
 */
function PartyLearningsPanel({ text }) {
  if (!text) return null
  return (
    <div className="reconcile-party-learnings">
      <div className="reconcile-party-learnings-h">
        <span className="reconcile-issues-lbl">Party-specific learnings</span>
        <CopyButton text={text} />
      </div>
      <Markdown text={text} />
    </div>
  )
}

/**
 * What actually went wrong or needed a fallback while running *this specific*
 * reconciliation — a file that couldn't be deterministically extracted and
 * fell back to Claude's own reading, a refusal, an empty response. Separate
 * from the summary and visually its own block, so a clean run (no issues)
 * shows nothing here at all rather than an empty section.
 */
function IssuesPanel({ issues }) {
  if (!issues?.length) return null
  return (
    <div className="reconcile-issues">
      <span className="reconcile-issues-lbl">Issues faced this run</span>
      <ul>
        {issues.map((iss, i) => (
          <li key={i}>
            <span className="c-sub">{iss.stage}</span> — {iss.message}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The reconciliation deliverable itself — a 7-tab Excel workbook, not chat
 * text, so this is a download action rather than something rendered inline.
 * A plain `<a href>` can't carry the auth header the endpoint needs, so this
 * goes through `api.downloadReconciliationExcel` (fetch → blob → synthetic
 * click), same as every other proxied download on this screen — hence its
 * own busy/error state instead of just being a link.
 */
function DownloadExcelButton({ runId, fileName }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const download = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.downloadReconciliationExcel(runId, fileName)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingRight: 6, paddingTop: 4, paddingBottom: 4 }}>
        {/* The workbook's own filename, not squeezed into the button label —
            it can run long (both source filenames plus "Reconciliation - "),
            and a button that grows to fit an arbitrary sentence stops
            reading as a button. Free to wrap; nothing here truncates it. */}
        {fileName && (
          // .c-sub's own margin-top: 3px shifts it down from true center
          // relative to the button beside it, even with alignItems: center
          // on the row — overridden to 0 here specifically.
          <span className="c-sub" style={{ whiteSpace: 'normal', overflow: 'visible', flex: 1, minWidth: 0, marginTop: 0 }}>
            {fileName}
          </span>
        )}
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={download}
          disabled={busy}
          style={{ marginLeft: 'auto', flexShrink: 0 }}
        >
          <Icon name="doc" size={12} />
          {busy ? 'Downloading…' : 'Download Excel'}
        </button>
      </div>
      {error && <div className="c-sub" style={{ color: 'var(--danger, #c0392b)', marginTop: 6 }}>{error}</div>}
    </div>
  )
}

function FileSlot({ label, hint, file, onPick }) {
  const inputRef = useRef(null)
  return (
    <label className="reconcile-slot">
      <span className="reconcile-slot-lbl">{label}</span>
      <div
        className={`reconcile-drop${file ? ' has-file' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          const f = e.dataTransfer.files?.[0]
          if (f) onPick(f)
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])}
        />
        {file ? (
          <>
            <span className="reconcile-drop-name">{file.name}</span>
            <span className="c-sub">{Math.max(1, Math.round(file.size / 1024))} KB — click to replace</span>
          </>
        ) : (
          <>
            <span className="reconcile-drop-name">Click or drop a file</span>
            <span className="c-sub">{hint}</span>
          </>
        )}
      </div>
    </label>
  )
}

/**
 * A History row for a batch job that hasn't finalized yet (`kind:
 * 'pendingBatch'`) — its own "Check status" here, independent of the live
 * Bulk panel's own state, so a batch submitted in an earlier visit (or by
 * someone who refreshed mid-wait) is still checkable: this row's `jobId`
 * comes from the server's own state, not anything held in this page.
 */
function PendingBatchRow({ job, expanded, onToggle, onChecked, onToast }) {
  const canAct = useCanAct()
  const [checking, setChecking] = useState(false)
  const [checkError, setCheckError] = useState(null)
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState(null)

  const check = async () => {
    setChecking(true)
    setCheckError(null)
    try {
      const out = await api.checkReconciliationBatch(job.jobId)
      onToast(describeBatchStatus(out))
      onChecked()
    } catch (e) {
      setCheckError(e.message)
    } finally {
      setChecking(false)
    }
  }

  const cancel = async () => {
    setCancelling(true)
    setCancelError(null)
    try {
      await api.cancelReconciliationBatch(job.jobId)
      onChecked()
    } catch (e) {
      setCancelError(e.message)
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div className={`reconcile-run${expanded ? ' open' : ''}`}>
      <button type="button" className="reconcile-run-h" onClick={onToggle}>
        <span className="pill p-info">
          <span className="d" />
          {job.processingStatus === 'canceling' ? 'cancelling' : 'pending'}
        </span>
        <span className="c-sub">{runTimeText(job.createdAt)}</span>
        <SampleTag show={isSampleId(job._id)} />
        {job.model && <span className="c-sub">{job.model}</span>}
        <span className="c-sub" style={{ marginLeft: 'auto' }}>
          {job.oursFile?.filename ?? '—'} vs {job.clientFile?.filename ?? '—'}
        </span>
      </button>
      {expanded && (
        <div className="reconcile-run-body">
          <div className="c-sub" style={{ marginBottom: 10, whiteSpace: 'normal' }}>
            Anthropic's own status: {job.processingStatus}. Batches can take minutes to 24 hours — nothing
            checks this on its own, so check back whenever you want to know. This works even for a batch
            submitted in an earlier visit, or before a refresh.
          </div>
          {canAct && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-sm" onClick={check} disabled={checking}>
              {checking ? 'Checking…' : 'Check status'}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={cancel} disabled={cancelling}>
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          </div>
          )}
          {checkError && (
            <div className="c-sub" style={{ color: 'var(--danger, #c0392b)', marginTop: 10 }}>
              {checkError}
            </div>
          )}
          {cancelError && (
            <div className="c-sub" style={{ color: 'var(--danger, #c0392b)', marginTop: 10 }}>
              {cancelError}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** One row of history — read straight from `reconciliationruns`, not from
 *  any live call. Success and failure share a row shape so the list reads as
 *  one timeline rather than two different tables. */
function RunRow({ run, expanded, onToggle }) {
  const ok = run.status === 'success'
  return (
    <div className={`reconcile-run${expanded ? ' open' : ''}`}>
      <button type="button" className="reconcile-run-h" onClick={onToggle}>
        <span className={`pill ${ok ? 'p-ok' : 'p-bad'}`}>
          <span className="d" />
          {ok ? 'success' : run.failureReason ?? 'failure'}
        </span>
        {run.viaBatch && <span className="pill p-mute" title="Submitted via the Batch API">batch</span>}
        <span className="c-sub">{runTimeText(run.createdAt)}</span>
        <SampleTag show={isSampleId(run._id)} />
        {run.model && <span className="c-sub">{run.model}</span>}
        {ok && (
          <span className="c-sub">
            {run.usage.inputTokens.toLocaleString('en-IN')} in · {run.usage.outputTokens.toLocaleString('en-IN')} out
          </span>
        )}
        <span className="c-sub" style={{ marginLeft: 'auto' }}>
          {run.oursFile?.filename ?? '—'} vs {run.clientFile?.filename ?? '—'}
        </span>
      </button>
      {expanded && (
        <div className="reconcile-run-body">
          {ok ? (
            <>
              {run.excelS3Key ? (
                <DownloadExcelButton runId={run._id} fileName={run.excelFilename} />
              ) : run.summary ? (
                // Legacy row, from before the reconciler produced an Excel
                // deliverable — it only ever has the old chat summary, so
                // that's still what renders for it.
                <>
                  <div className="reconcile-copy-row">
                    <CopyButton text={run.summary} />
                  </div>
                  <Markdown text={run.summary} />
                  <PartyLearningsPanel text={run.partyLearnings} />
                </>
              ) : (
                // A new-style run whose workbook built fine but couldn't be
                // saved to S3 (see its own "excel_not_stored" issue below) —
                // nothing to download, and no legacy summary to fall back on.
                <div className="note" style={{ marginTop: 0 }}>
                  The workbook couldn't be saved for this run.
                </div>
              )}
              <div className="reconcile-stats">
                {/* Input/output tokens already show in the row header above,
                    which stays visible while expanded — repeating them here
                    would just be the same numbers twice on one card. */}
                <span className="c-sub">
                  {run.usage.cacheReadTokens > 0 && `${run.usage.cacheReadTokens.toLocaleString('en-IN')} cached · `}
                  Time taken: {(run.durationMs / 1000).toFixed(1)}s
                </span>
              </div>
              <IssuesPanel issues={run.issues} />
            </>
          ) : (
            <div className="note" style={{ marginTop: 0 }}>{run.errorMessage ?? 'No further detail recorded.'}</div>
          )}
        </div>
      )}
    </div>
  )
}

/** One empty ours/client pair — the shape a fresh row (and the fresh form
 *  after a successful submit) starts from. */
const EMPTY_SET = () => ({ oursFile: null, clientFile: null })

export default function LedgerReconciler() {
  // Accountants can read the test bench's runs; only admins upload new pairs.
  const canAct = useCanAct()
  // Which submit panel shows — the test bench (two uploaded files) or the
  // real flow (a real, already-collected LedgerRequest). Independent of
  // `historyFilter` below; the two combine to answer "which runs show".
  const [mode, setMode] = useState('tracker')

  // Every reconciliation goes through the Batch API — see the plan that
  // removed the synchronous "Normal" mode this file used to also offer.
  // `sets` holds one or more ours/client pairs, submitted together in one
  // "Submit as a batch" press; each still becomes its own independent
  // ReconciliationBatchJob, exactly as a single submission always has, so
  // History below needs no changes to track any number of them.
  const [sets, setSets] = useState([EMPTY_SET()])

  const [bulkSubmitting, setBulkSubmitting] = useState(false)
  // Per-submission outcomes for the sets just pressed "Submit" on — cleared
  // on the next submit. Not a live status panel: once submitted, a set's
  // ongoing status lives in History below like every other pending batch.
  const [submitResults, setSubmitResults] = useState([])

  const [history, setHistory] = useState(null)
  const [openRunId, setOpenRunId] = useState(null)
  const [historyFilter, setHistoryFilter] = useState('all')

  // A small stack of toasts (usually just one) — each "Check status" press
  // announces its result here so it's visible without opening the row/panel
  // that triggered it. Self-dismissing; a manual close isn't worth the UI
  // for something this transient.
  const [toasts, setToasts] = useState([])
  const pushToast = useCallback(({ kind, message }) => {
    const id = Date.now() + Math.random()
    setToasts((ts) => [...ts, { id, kind, message }])
    setTimeout(() => setToasts((ts) => ts.filter((t) => t.id !== id)), 4500)
  }, [])

  const loadHistory = useCallback(async () => {
    try {
      const runs = await api.listReconciliationRuns()
      setHistory(runs ?? [])
    } catch {
      // History is a nice-to-have next to the submit panel above it — a
      // failed fetch here should not block anyone from still submitting.
      setHistory([])
    }
  }, [])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const setSetAt = (i, patch) => setSets((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)))
  const addSet = () => setSets((prev) => [...prev, EMPTY_SET()])
  const removeSet = (i) => setSets((prev) => prev.filter((_, idx) => idx !== i))

  const completeSets = sets.filter((s) => s.oursFile && s.clientFile)
  const canSubmitBulk = completeSets.length > 0 && !bulkSubmitting

  /**
   * One `submitReconciliationBatch` call, no matter how many complete sets
   * there are — every one of them rides in the same real Anthropic batch
   * (see the backend's own `submitReconciliationBatch`), rather than each
   * set paying for a separate batch submission. The backend still gives
   * each set its own outcome in `results` (in the same order as
   * `completeSets`): a set failing a $0 gate (bad file type, say) is
   * reported there without stopping the others from actually submitting.
   * Incomplete rows (missing a file) are silently skipped rather than
   * blocking the whole submit — `canSubmitBulk` already requires at least
   * one complete row to press this at all.
   */
  const submitBulk = async () => {
    setBulkSubmitting(true)
    setSubmitResults([])
    try {
      const out = await api.submitReconciliationBatch({ sets: completeSets })
      const results = out.results.map((r, i) => ({
        oursName: completeSets[i].oursFile.name,
        clientName: completeSets[i].clientFile.name,
        ok: r.ok,
        message: r.ok ? null : r.message,
      }))
      setSubmitResults(results)
      // A clean sweep resets the form to one empty row, same as a single
      // submission always used to clear itself; any failure(s) leave the
      // form as-is so the failed pair(s) are still right there to fix and
      // resubmit rather than having to be re-picked from scratch.
      if (results.every((r) => r.ok)) setSets([EMPTY_SET()])
    } catch (e) {
      setSubmitResults(completeSets.map((s) => ({ oursName: s.oursFile.name, clientName: s.clientFile.name, ok: false, message: e.message })))
    } finally {
      setBulkSubmitting(false)
      // Every submission, success or $0-gate failure, writes a
      // reconciliationruns/reconciliationBatchJob row immediately.
      loadHistory()
    }
  }

  // 'pending' means every still-in-flight batch, regardless of Anthropic's
  // own in_progress/canceling distinction — that split matters inside a
  // pending row itself, not as a reason to split the filter two ways.
  const filteredHistory = (history ?? []).filter((r) => {
    // Test-bench runs only — a real client's run carries `clientCode` (see
    // reconciliationRun.model.js) and lives on the Client tracker instead.
    if (r.clientCode) return false
    if (historyFilter === 'all') return true
    if (historyFilter === 'pending') return r.kind === 'pendingBatch'
    return r.kind === 'run' && r.status === historyFilter
  })

  return (
    <section className="screen">
      <div className="topbar">
        <div>
          <h1 className="page-title">Ledger Reconciler</h1>
          <div className="page-sub">
            {mode === 'tracker'
              ? "Every client's reconciliation, one row per collection cycle — what's ready, what's running, and what each run found."
              : 'Try the reconciler on any two ledgers without touching a real client. Nothing uploaded here is stored; only each run\'s result is kept below.'}
          </div>
        </div>
        <Segmented options={MODE_OPTIONS} value={mode} onChange={setMode} title="Client tracker or test bench" />
      </div>

      {mode === 'tracker' ? (
        <ReconciliationTracker />
      ) : (
        <>
          {canAct && (
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-head">
              <span className="panel-title">Ledgers to compare</span>
              <span className="c-sub">PDF, Excel/CSV, or a photo of a statement — one pair per row</span>
            </div>
            {sets.map((s, i) => (
              <div key={i} className="panel-pad reconcile-slots" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: 12, flex: 1 }}>
                  <FileSlot
                    label="Our ledger"
                    hint="the export from Gati"
                    file={s.oursFile}
                    onPick={(f) => setSetAt(i, { oursFile: f })}
                  />
                  <FileSlot
                    label="Client's ledger"
                    hint="the statement they sent"
                    file={s.clientFile}
                    onPick={(f) => setSetAt(i, { clientFile: f })}
                  />
                </div>
                {sets.length > 1 && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeSet(i)} title="Remove this pair">
                    Remove
                  </button>
                )}
              </div>
            ))}

            <div className="panel-pad" style={{ paddingTop: 0, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" disabled={!canSubmitBulk} onClick={submitBulk}>
                {bulkSubmitting
                  ? 'Starting…'
                  : completeSets.length > 1
                    ? `Reconcile ${completeSets.length} pairs`
                    : 'Reconcile'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={addSet}>
                + Add another pair
              </button>
              <span className="c-sub" style={{ marginTop: 0 }}>
                Runs in the background — results land in Test runs below, usually within minutes.
              </span>
            </div>
          </div>
          )}

          {submitResults.length > 0 && (
            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-head">
                <span className="panel-title">Just started</span>
              </div>
              <div className="panel-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {submitResults.map((r, i) => (
                  <div key={i} className="c-sub">
                    <span className={`pill ${r.ok ? 'p-ok' : 'p-bad'}`}>
                      <span className="d" />
                      {r.ok ? 'started' : 'not started'}
                    </span>{' '}
                    {r.oursName} vs {r.clientName}
                    {!r.ok && <span style={{ color: 'var(--red)' }}> — {r.message}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="panel">
            <div className="panel-head">
              <span className="panel-title">Test runs</span>
              <Segmented
                options={HISTORY_FILTER_OPTIONS}
                value={historyFilter}
                onChange={setHistoryFilter}
                title="Show only runs with this outcome"
              />
            </div>
            <div className="panel-pad">
              {history === null && <div className="c-sub">Loading…</div>}
              {history !== null && filteredHistory.length === 0 && (
                <div className="note" style={{ marginTop: 0 }}>
                  {historyFilter === 'all' ? 'No test runs yet — upload a pair above to start one.' : 'No test runs with this outcome.'}
                </div>
              )}
              {filteredHistory.map((r) =>
                r.kind === 'pendingBatch' ? (
                  <PendingBatchRow
                    key={r._id}
                    job={r}
                    expanded={openRunId === r._id}
                    onToggle={() => setOpenRunId((id) => (id === r._id ? null : r._id))}
                    onChecked={loadHistory}
                    onToast={pushToast}
                  />
                ) : (
                  <RunRow
                    key={r._id}
                    run={r}
                    expanded={openRunId === r._id}
                    onToggle={() => setOpenRunId((id) => (id === r._id ? null : r._id))}
                  />
                )
              )}
            </div>
          </div>
        </>
      )}

      <div className="toast-wrap">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            {t.message}
          </div>
        ))}
      </div>
    </section>
  )
}
